<template>
    <div class="globe-with-markers" ref="canvasParent">

        <Markers 
            v-if="enabled"
            :markers="markersToDisplay"
            @markerClicked="handleMarkerClicked"
            :markerMergeRadius="35"
            class="globe-with-markers__markers"
            :pointAtWhichPopupsShouldDrawUpward="500"/>

        <div v-if="enabled && selectedMarker && selectedMarker.type === 'datalayer'" class="datalayer-marker-popup" :class="'datalayer-marker-popup--'+selectedMarker.colorTheme">
            <div class="case-marker-popup__close-btn" @click="selectedMarkerId = null"></div>
            <div class="case-marker-popup__media">

                <!--<img v-if="selectedMarker.data.image" :width="selectedMarker.data.image.width" :height="selectedMarker.data.image.height" :src="imageUrl(selectedMarker.data.image)" />-->
                
                <PreloadedImage v-if="selectedMarker.data.image" :src="imageUrl(selectedMarker.data.image)"/>

            </div>
            <div class="case-marker-popup__heading">{{selectedMarker.data.heading}}</div>
            <div class="case-marker-popup__text">
                <div v-html="selectedMarker.data.text"></div>
                <div class="case-marker-popup__source" v-html="selectedMarker.data.source"></div>
            </div>
        </div>

    </div>
</template>

<script>
import config from '@/config.js';
import {Scene, PerspectiveCamera, WebGLRenderer, Object3D, Math, Vector3, SphereGeometry, AmbientLight, /*CylinderGeometry,*/ TextureLoader, MeshPhongMaterial, Mesh, PointLight} from 'three';
import PreloadedImage from '@/components/PreloadedImage.vue';
import Markers from '@/components/Markers';

// NOTE(Mølby): Three js variables. I'm putting them in module scope since vue does stuff to them that breaks three js if I put them in component state.
var scene;
var camera;
var renderer;
var parentTransform;
var markerRoot;
var markerObject;
var vectorWorldPos = new Vector3();
var vector = new Vector3();

export default {
	name: 'RealtimeGlobeWithMarkers',
	props: {
        enabled: Boolean,
		globeRotation: Object,
        markers: Array,
        selectedCaseId: Number,
        viewCaseLabel: String
	},
    components: {PreloadedImage, Markers},
	data()
	{
		return {
            initialized: false,
            selectedMarkerId: null
        }
	},
    watch:
    {
        markers()
        {
            this.selectedMarkerId = null;
        }
    },
    computed:
    {
        markersToDisplay()
        {
            var result = [];


            this.markers.forEach((marker)=>{

                if ((!this.selectedCaseId) || (this.selectedCaseId === marker.data.caseId))
                {
                    result.push({
                        id: marker.id,
                        type: marker.type,
                        positionInfo: this.calcMarkerPosition(marker),
                        colorTheme: marker.colorTheme,
                        showPopupContent: (marker.type === 'case' && marker.id === this.selectedMarkerId),
                        popupName: marker.data.caseName,
                        popupLocation: marker.data.locationName,
                        //popupLinkRouterTo: '/', // '/denmark/'+this.selectedChallenge.id+'/'+challengeCase.id+'/',
                        popupLinkCallback: ()=>{this.$emit('open-case', marker)},
                        popupLinkText: this.viewCaseLabel
                    });
                }

            });

            return result;
        },
        selectedMarker()
        {
            var result = null;

            this.markers.forEach((marker)=>{
                if (marker.id === this.selectedMarkerId)
                {
                    result = marker;
                }
            });

            return result;
        }
    },
	mounted()
	{
        scene = new Scene();

        camera = new PerspectiveCamera(22, 835/835, 0.1, 1000);
        camera.position.z = 5.4;

        renderer = new WebGLRenderer({antialias:true, alpha: true});
        renderer.setSize(835, 835);

        this.$refs.canvasParent.appendChild(renderer.domElement);

        var sphereGeometry = new SphereGeometry( 1, 256, 256 );
        var globeTexture = null;
        var globeMaterial = new MeshPhongMaterial( { color: "#FFF", map: globeTexture } );
        // globeMaterial.wireframe = true;

        var globeSphere = new Mesh(sphereGeometry, globeMaterial);


        // NOTE(Mølby): Load all textures
        let textureLoader = new TextureLoader();
        textureLoader.crossOrigin = true;

        var textureAlbedoURI = 'textures/scaled/earth_albedo_no-oceans.jpg';
        var textureBumpURI = 'textures/scaled/earth_bump.jpg';
        var textureOceanMaskURI = 'textures/scaled/earth_land_ocean_mask.jpg';
        var textureOceanMaskSimpleURI = 'textures/scaled/earth_land_ocean_mask_simple_2.jpg';


        // NOTE(Mølby): Load main texture
        var promiseLoadMainTexture = new Promise((resolve)=>{
            textureLoader.load(
            textureAlbedoURI,
            function(texture) {
                texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
                globeMaterial.map = texture;
                globeMaterial.needsUpdate = true;
                resolve();
            });
        });


        // NOTE(Mølby): Apply heightmap to bump
        globeMaterial.bumpScale = 0.15;
        var promiseLoadBumpTexture = new Promise((resolve)=>{
            textureLoader.load(
                textureBumpURI,
                function(texture) {
                    texture.anisotropy =  renderer.capabilities.getMaxAnisotropy();
                    globeMaterial.bumpMap = texture;
                    globeMaterial.needsUpdate = true;
                    resolve();
                }
            );
        });

        // NOTE(Mølby): Apply ocean mask to specular
        globeMaterial.shininess = 1;
        globeMaterial.specular = {r:1,g:1,b:1};
        var promiseLoadOceanMaskTexture = new Promise((resolve)=>{
            textureLoader.load(
                textureOceanMaskURI,
                function(texture) {
                    texture.anisotropy =  renderer.capabilities.getMaxAnisotropy();
                    globeMaterial.specularMap = texture;
                    globeMaterial.needsUpdate = true;
                    resolve();
                }
            );
        });

        // NOTE(Mølby): Displace landmasses 
        globeMaterial.displacementScale = 0.01; // 0.007;
        var promiseLoadDisplaceTextureTexture = new Promise((resolve)=>{
            textureLoader.load(
                textureOceanMaskSimpleURI,
                function(texture) {
                    texture.anisotropy =  renderer.capabilities.getMaxAnisotropy();
                    globeMaterial.displacementMap = texture;
                    globeMaterial.needsUpdate = true;
                    resolve();
                }
            );
        });

        // NOTE(Mølby): Emit event when all textures are ready
        Promise.all([promiseLoadMainTexture, promiseLoadBumpTexture, promiseLoadOceanMaskTexture, promiseLoadDisplaceTextureTexture]).then(()=>{
            this.$emit('doneLoading');
        });


        parentTransform = new Object3D();
        scene.add(parentTransform);

        var globeTransform = new Object3D();
        parentTransform.add(globeTransform);

        globeTransform.add(globeSphere);

        // NOTE(Mølby): Setup lighting

        const light = new PointLight( 0xffffff, 0.45, 20);
        light.position.set(0.75, 1.25, 3.5);
        scene.add( light );

        const light3 = new AmbientLight( 0xffffff );
        light3.intensity = 0.75;
        scene.add( light3 );

        // NOTE(Mølby): Create marker object that can be transformed to determine marker locations
        markerRoot = new Object3D();
        globeTransform.add(markerRoot);

        // NOTE(Mølby): This is the thing to measure position off!
        markerObject = new Object3D();
        markerRoot.add(markerObject);
        markerObject.position.set(-1,0,0); // NOTE(Mølby): 1 equals surface of planet in the scale we work with...
        markerObject.scale.set(0.02,0.02,0.02);


        this.initialized = true;

        this.updateLoop();
    },
    unmounted()
    {
        // NOTE(Mølby): This would be the place to properly destroy Three JS. (This will never unmount though.)
    },
	methods:
	{
        imageUrl(image)
        {
            var url = config.umbracoMediaPath + image.src;
            return url;
        },
        handleMarkerClicked(markerInfo)
        {
          
            if (!markerInfo || this.selectedMarkerId === markerInfo.id )
            {
                this.selectedMarkerId = null;
            }
            else if (markerInfo.type === 'datalayer')
            {
                this.selectedMarkerId = markerInfo.id;
            }
          
        },
        calcMarkerPosition(marker)
        {
            if (!this.initialized) return '';

            // NOTE(Mølby): Make sure globe is rotated
            parentTransform.rotation.y = Math.degToRad(this.globeRotation.y);
            parentTransform.rotation.x = Math.degToRad(this.globeRotation.x);

            var offsetToMiddle = Math.degToRad(180);

            // NOTE(Mølby): Rotate markerRoot using coordinates 
            var x = Math.degToRad(marker.coordinates.long);
            var y = Math.degToRad(-marker.coordinates.lat);
            
            markerRoot.rotation.set = (0,0,0);
            markerRoot.rotation.y = offsetToMiddle + x;
            markerRoot.rotation.z = y;

            markerObject.getWorldPosition(vectorWorldPos);
            vector.set(vectorWorldPos.x, vectorWorldPos.y, vectorWorldPos.z);

            // NOTE(Mølby): Map to 2D screen space
            vector.project( camera );

            var canvasWidth = 835;
            var canvasHeight = 835;

            vector.x = window.Math.round( (   vector.x + 1 ) * canvasWidth  / 2 );
            vector.y = window.Math.round( ( - vector.y + 1 ) * canvasHeight / 2 );
            vector.z = 0;

            var x2d = vector.x;
            var y2d = vector.y;
            var opacity = 1;
            var pointerEvents = 'pointer-events:all';
            var isHidden = (vectorWorldPos.z < 0.2);
            if (isHidden)
            {
                opacity = 0;
                pointerEvents = 'pointer-events:none';
            }

            return {
                x: x2d,
                y: y2d,
                opacity: opacity,
                pointerEvents: pointerEvents
            }
        },
        updateLoop()
        {
            if (this.enabled)
            {
                renderer.render(scene, camera);
    
                // NOTE(Mølby): Make sure globe is rotated
                parentTransform.rotation.y = Math.degToRad(this.globeRotation.y);
                parentTransform.rotation.x = Math.degToRad(this.globeRotation.x);
            }

            requestAnimationFrame(this.updateLoop);
        }
    }
}
</script>